import React from 'react';

export default function ErrorModal({
  error,
  onClose,
  onRetry,
  canRetry = false,
}) {
  if (!error) return null;

  const titleId = 'global-error-modal-title';
  const messageId = 'global-error-modal-message';
  const isNetworkError = ['network', 'connection', 'offline'].includes(
    String(error?.type || '').toLowerCase(),
  );
  const isSaveError = String(error?.type || '').includes('save_error');
  const containerClassName = [
    'error-modal__container',
    isNetworkError ? 'error-modal__container--network' : 'error-modal__container--default',
  ].join(' ');

  return (
    <div style={backdropStyle} className="error-modal__backdrop">
      <div
        style={modalStyle}
        className={containerClassName}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
      >
        {isNetworkError && (
          <div
            className="error-modal__network-indicator"
            role="img"
            aria-label="Conexão perdida"
          >
            <span className="error-modal__wifi-arc error-modal__wifi-arc--outer" />
            <span className="error-modal__wifi-arc error-modal__wifi-arc--middle" />
            <span className="error-modal__wifi-arc error-modal__wifi-arc--inner" />
            <span className="error-modal__wifi-dot" />
            <span className="error-modal__wifi-slash" />
          </div>
        )}
        {isSaveError && (
          <div
            className="error-modal__error-indicator"
            role="img"
            aria-label="Erro ao salvar"
          >
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#dc3545" strokeWidth="2" fill="#dc3545" opacity="0.1" />
              <path d="M12 8v4m0 4h.01" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
        )}
        <h4 id={titleId}>{error.title}</h4>
        <p id={messageId}>{error.message}</p>

        <div
          style={actionsStyle}
          className={isNetworkError ? 'error-modal__actions--network' : ''}
        >
          <button
            type="button"
            className="btn btn-secondary error-modal__button"
            onClick={onClose}
          >
            Fechar
          </button>

          {canRetry && (
            <button
              type="button"
              className="btn btn-primary error-modal__button"
              onClick={onRetry}
            >
              Tentar novamente
            </button>
          )}
        </div>
      </div>
      <style>
        {`
          .error-modal__container h4 {
            margin: 0;
            line-height: 1.35;
          }

          .error-modal__container p {
            margin: 12px 0 0;
            line-height: 1.5;
            word-break: break-word;
          }

          .error-modal__container--network h4,
          .error-modal__container--network p {
            text-align: center;
          }

          .error-modal__container--default h4,
          .error-modal__container--default p {
            text-align: left;
          }

          .error-modal__container--network p {
            margin-left: auto;
            margin-right: auto;
            max-width: 320px;
          }

          .error-modal__network-indicator {
            position: relative;
            width: 80px;
            height: 60px;
            margin: 0 auto 14px;
          }

          .error-modal__wifi-arc {
            position: absolute;
            left: 50%;
            border: 3px solid transparent;
            border-top-color: #6c757d;
            border-radius: 999px;
            transform: translateX(-50%);
            animation: error-modal-signal-pulse 1.6s ease-in-out infinite;
          }

          .error-modal__wifi-arc--outer {
            width: 74px;
            height: 40px;
            top: 2px;
            animation-delay: 0s;
          }

          .error-modal__wifi-arc--middle {
            width: 52px;
            height: 28px;
            top: 10px;
            animation-delay: 0.2s;
          }

          .error-modal__wifi-arc--inner {
            width: 30px;
            height: 16px;
            top: 18px;
            animation-delay: 0.4s;
          }

          .error-modal__wifi-dot {
            position: absolute;
            left: 50%;
            bottom: 10px;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #6c757d;
            transform: translateX(-50%);
            animation: error-modal-signal-pulse 1.6s ease-in-out infinite;
            animation-delay: 0.55s;
          }

          .error-modal__wifi-slash {
            position: absolute;
            left: 50%;
            top: 0;
            width: 6px;
            height: 60px;
            border-radius: 3px;
            background: #dc3545;
            transform: translateX(-50%) rotate(34deg);
            transform-origin: center center;
            animation: error-modal-slash-blink 1s ease-in-out infinite;
          }

          .error-modal__button {
            min-width: 130px;
          }

          .error-modal__actions--network {
            justify-content: center;
          }

          .error-modal__button:focus-visible {
            outline: 3px solid #0d6efd;
            outline-offset: 2px;
          }

          @keyframes error-modal-signal-pulse {
            0%, 100% {
              opacity: 0.35;
            }
            50% {
              opacity: 1;
            }
          }

          @keyframes error-modal-slash-blink {
            0%, 100% {
              opacity: 1;
              background: #dc3545;
            }
            50% {
              opacity: 0.55;
              background: #ff6b6b;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .error-modal__wifi-arc,
            .error-modal__wifi-dot,
            .error-modal__wifi-slash {
              animation: none;
            }
          }

          @media (max-width: 480px) {
            .error-modal__container {
              width: calc(100% - 24px) !important;
              padding: 20px !important;
            }

            .error-modal__button {
              min-width: 100%;
            }
          }
        `}
      </style>
    </div>
  );
}

const backdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

const modalStyle = {
  background: '#fff',
  padding: '24px',
  borderRadius: '8px',
  width: '400px',
  maxWidth: '90%',
  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
};

const actionsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  marginTop: '20px',
};
